import { Injectable } from '@angular/core';
import { WizardModel } from '../wizard-model';
import { CodingOption, ConfigurationTemplate, QuantitySelectionOption, TileSelectionOption } from '../wizard-model.interface';
import { OverlayService } from '@shared/overlay/overlay.service';
import { ConfigurationCreationService } from '../../service/configuration-creation.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { RouteNavigationService } from '@services/route-navigation.service';
import { filter, firstValueFrom, lastValueFrom } from 'rxjs';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleVO } from '@article/model/vo/article.vo';
import { McsGdsProjectCreationProcessor } from '../../service/mcs-gds-project-creation.processor';
import { MiniTerminalBlocksArticleManager } from '@article/manager/mini-terminal-blocks-article.manager';
import { BaseArticleProjectProcessorFactory } from '@article/model/base-article-project-processor-factory';
import { ArticleContainerAttachedService } from '@project/components/article/service/article-container-attached.service';
import { ProjectModel } from '@project/model/project.model';
import { ProjectModelService } from '@project/service/project-model.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { MiniTerminalBlocksProjectCreationService } from '../../service/mini-terminal-blocks-project-creation.service';
import { ProjectChangedType } from '@project/model/project-changed';
import { Log } from '@shared/debug/log';

@Injectable()
export class McsMaxiModel extends WizardModel {
  public readonly type = "MCS_MAXI";

  private _selectedOptions: {[option: string]: string | string[] | number} = {};

  constructor(
    private alertHelper: AlertHelper,
    private miniTerminalBlocksArticleManager: MiniTerminalBlocksArticleManager,
    private baseArticleProjectProcessorFactory: BaseArticleProjectProcessorFactory,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private articleContainerAttachedService: ArticleContainerAttachedService,
    private miniTerminalBlocksProjectCreationService: MiniTerminalBlocksProjectCreationService,
    private configurationCreationService: ConfigurationCreationService,
    private articleDataService: ArticleDataService,
    private overlayService: OverlayService,
    private routeNavigationService: RouteNavigationService,
  ) {
    super();
  }

  public async create(): Promise<void> {
    if (this.creationStarted) {
      return;
    }

    this.creationStarted = true;
    const processor = this.getMcsGdsProjectCreationProcessor();

    try {
      await this.createProject(processor);
    } catch (error) {
      Log.error('Could not create configuration: ', error);
    } finally {
      processor.clear();
      this.creationStarted = false;
    }
  }

  private getMcsGdsProjectCreationProcessor(): McsGdsProjectCreationProcessor {
    return new McsGdsProjectCreationProcessor(
      this.projectModel,
      this.projectModelService,
      this.alertHelper,
      this.miniTerminalBlocksArticleManager,
      this.baseArticleProjectProcessorFactory,
      this.articleContainerAttachedService,
      this.miniTerminalBlocksProjectCreationService
    );
  }

  private async createProject(processor: McsGdsProjectCreationProcessor): Promise<void> {
    await this.overlayService.doWithOverlay(async () => {
      await this.configurationCreationService.prepareNewConfiguration(this.metaData);

      const articles: ArticleVO = await lastValueFrom(this.articleDataService.loadArticleByArticleNumber(this._selectedOptions['article'] as string));
      const projectFinishedPromise = firstValueFrom(this.projectModel.projectChanged$.pipe(filter(p => p.type === ProjectChangedType.McsGdsProjectCreated)));
      await this.routeNavigationService.toConfigurator();
      await processor?.assembleProject(articles, "MCS");
      await projectFinishedPromise;
    },
    OverlayBackground.color);
  }

  public selectValue(step: number, value: string | number): void {
    const currentStepConfig = this.config.steps[step];
    if (step < 0 || !currentStepConfig) {
      return;
    }
    const currentStepTitle = currentStepConfig.id;
    this._selectedOptions[currentStepTitle] = value;
  }

  protected createConfig(): ConfigurationTemplate {
    return {
      id: "create-project-mcs-maxi",
      neededPermission: "DEV_TEST",
      type: 'assistant',
      imageUrl: "ng-assets/img/mcs-maxi-assistant/mcs_maxi.webp",
      label: "configurationCreation.type.mcs_maxi",
      steps: [
        {
          type: 'meta',
          id: 'meta',
          label: "configurationCreation.progress.configuration_data",
        },
        {
          type: 'tileSelection',
          id: 'rating',
          label: "createConfigurationAssistant.progress.rating_data.label",
          title: "createConfigurationAssistant.question.rating_data",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMcsMaxiTerminal.option.rating.maxi_6",
              id: "maxi_6",
              value: 6,
              imageUrl: "ng-assets/img/mcs-maxi-assistant/1-Maxi6.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.rating.maxi_16",
              id: "maxi_16",
              value: 16,
              imageUrl: "ng-assets/img/mcs-maxi-assistant/1-Maxi16.jpg",
              size: "L"
            }
          ]
        },
        {
          type: 'tileSelection',
          id: "connection_solution",
          label: "createConfigurationAssistant.progress.connection_solution.label",
          title: "createConfigurationAssistant.question.connection_solution",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMcsMaxiTerminal.option.connection_solution.wire_to_board",
              id: "wire-to-board",
              value: "wire-to-board",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/2-1_Wire-to-board.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.connection_solution.wire_to_wire",
              id: "wire-to-wire",
              value: "wire-to-wire",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/2-2_Wire-to-wire.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.connection_solution.board_to_wire",
              id: "board-to-wire",
              value: "board-to-wire",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/2-3_Board-to-wire.jpg",
              size: "L"
            }
          ]
        },
        {
          type: 'tileSelection',
          id: 'conductor_entry',
          label: "createConfigurationAssistant.progress.conductor_entry.label",
          title: "createConfigurationAssistant.question.conductor_entry",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMcsMaxiTerminal.option.conductor_entry.standing",
              id: "standing",
              value: "standing",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/3-1_Gerade-stehend.jpg",
              size: "L"
            },
            {
              label: 'createMcsMaxiTerminal.option.conductor_entry.lying_top',
              id: "lying_top",
              value: "lying_top",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/3-2_Abgewinkelt-liegend_oben.jpg",
              size: "L"
            },
            {
              label: 'createMcsMaxiTerminal.option.conductor_entry.lying_bottom',
              id: "lying_bottom",
              value: "lying_bottom",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/3-3_Abgewinkelt-liegend_unten.jpg",
              size: "L"
            }
          ]
        },
        {
          type: 'tileSelection',
          id: 'locking_system',
          label: "createConfigurationAssistant.progress.locking_system.label",
          title: "createConfigurationAssistant.question.locking_system",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMcsMaxiTerminal.option.none",
              id: "none",
              value: "none",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/4-1_Ohne.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.locking_system.frontal",
              id: "frontal",
              value: "frontal",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/4-2_Frontal.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.locking_system.center",
              id: "center",
              value: "center",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/4-3_Mittig.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.locking_system.lateral",
              id: "lateral",
              value: "lateral",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/4-4_Seitlich.jpg",
              size: "L"
            },
          ]
        },
        {
          type: 'quantitySelection',
          id: "poles_amount",
          label: "createConfigurationAssistant.progress.poles_amount.label",
          title: "createConfigurationAssistant.question.poles_amount",
          info: "createConfigurationAssistant.question.potential_count.max",
          getOptions: (): Promise<QuantitySelectionOption[]> => Promise.resolve([ {
            max: 9,
            min: 2,
          } ]),
        },
        {
          type: 'coding',
          id: "coding",
          label: "createConfigurationAssistant.progress.coding.label",
          title: "createConfigurationAssistant.question.coding",
          getOptions: async(): Promise<CodingOption[]> => this.getCodingFingerOptions()
        },
        {
          type: 'tileSelection',
          id: "plug_color",
          label: "createConfigurationAssistant.progress.plug_color.label",
          title: "createConfigurationAssistant.question.plug_color",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMcsMaxiTerminal.option.plug_color.lightgray",
              id: "lightgray",
              value: "lightgray",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/7-1_Lichtgrau.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.plug_color.black",
              id: "black",
              value: "black",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/7-2_Schwarz.jpg",
              size: "L"
            }
          ]
        },
        {
          type: 'tileSelection',
          id: 'accessories',
          label: "createConfigurationAssistant.progress.accessories.label",
          title: "createConfigurationAssistant.question.accessories",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createMcsMaxiTerminal.option.none",
              id: "none",
              value: "none",
              imageUrl: "",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.accessory.strain_relief_bridge",
              id: "strain_relief_bridge",
              value: "strain_relief_bridge",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/8-Zugentlastung.jpg",
              size: "L"
            },
            {
              label: "createMcsMaxiTerminal.option.accessory.mounting_adapter",
              id: "mounting_adapter",
              value: "mounting_adapter",
              imageUrl: "ng-assets/img/mcs-maxi-assistant/8-Montageadapter.jpg",
              size: "L"
            }
          ]
        },
        {
          type: 'tileSelection',
          id: 'article',
          label: "createConfigurationAssistant.progress.article.label",
          title: "createConfigurationAssistant.question.article",
          getOptions: async(): Promise<TileSelectionOption[]> => {
            return [
              this.getArticleOption("831-1104/038-000"),
              this.getArticleOption("831-3604"),
            ]
          },
        }
      ]
    }
  }

  private getArticleOption(articleNo: string): TileSelectionOption {
    return {
      label: articleNo,
      skipLabelTranslation: true,
      id: articleNo,
      value: articleNo,
      imageUrl: `ng-assets/img/mcs-maxi-assistant/article/${articleNo.replace('/', '_')}.jpg`,
      size: "L"
    }
  }

  private getCodingFingerOptions(): CodingOption[] {
    return new Array(this._selectedOptions['poles_amount']).fill('').map((_, index) => ({index: index + 1, isSelected: false}));
  }
}
